import { CommonActions, useFocusEffect } from '@react-navigation/native';
import { Button, HStack, Spinner, VStack } from 'native-base';
import React, { useCallback, useEffect, useRef } from 'react';
import { Platform, StatusBar, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import ContentBlockRenderer from 'app/components/ContentBlockRenderer';
import CPAlert from 'app/components/CPAlert';
import CustomHeaderBackButton from 'app/components/CustomHeaderBackButton';
import LinkTicketsPrompt from 'app/components/LinkTicketsPrompt';
import Loading from 'app/components/Loading';
import Screen from 'app/components/Screen';
import ShareButton from 'app/components/ShareButton';
import { useGetProductScreenBlocksQuery } from 'app/generated/hygraph';
import useMetaData from 'app/hooks/useMetaData';
import useRootNavigation from 'app/hooks/useRootNavigation';
import { getRouteParam } from 'app/navigation/navigationHelper';
import { ProductStackParamList } from 'app/navigation/ProductStackNavigator';
import { RootScreenProps } from 'app/navigation/RootNavigator';
import { useI18n } from 'app/providers/I18nProvider';
import ProductProvider, { useProductContext } from 'app/providers/ProductProvider';
import ProductHero from 'app/screens/ProductScreen/ProductHero';
import ProductIntro from 'app/screens/ProductScreen/ProductIntro';
import ProductLogo from 'app/screens/ProductScreen/ProductLogo';
import HygraphService from 'app/services/HygraphService';
import { getUrl } from 'app/services/ProductHelper';
import { tID } from 'app/services/TestHelper';

const { client } = HygraphService();

// iOS 26 places native header buttons in a 44pt slot, 20pt from the edge. The product hero
// has its own (non-native) header, so on iOS 26+ its back button uses the same slot to line up
// with the native back button on the other screens (e.g. Attraction). No background: the
// arrow sits directly on the hero image, as before.
const hasGlassHeaderButtons = Platform.OS === 'ios' && parseInt(String(Platform.Version), 10) >= 26;
const styles = StyleSheet.create({
  backButton: {
    height: 48,
    width: 48,
  },
  alignedBackButton: {
    height: 44,
    width: 44,
    marginLeft: 20,
    paddingHorizontal: 0,
    justifyContent: 'center',
  },
});

export const PRODUCT_BLOCK_SPACE = 10;

export type ProductScreenProps = RootScreenProps<ProductStackParamList, 'Product'>;

export default function ProductScreen({ navigation, route }: ProductScreenProps) {
  const productContentKey = getRouteParam(route, 'productId') ?? '';

  useEffect(() => {
    if (!productContentKey) {
      navigation.navigate('Destinations', undefined, { pop: true });
    }
  }, [navigation, productContentKey]);

  if (!productContentKey) return null;

  return (
    <ProductProvider productContentKey={productContentKey}>
      <ProductScreenBody navigation={navigation} route={route} />
    </ProductProvider>
  );
}

function ProductScreenBody({ navigation, route }: ProductScreenProps) {
  const { productId: productContentKey } = route.params;
  const shouldReturnToPurchaseOrderDetails =
    route.params.returnTo === 'PurchaseOrderDetails' && !!route.params.entitlementKey;
  const rootNavigation = useRootNavigation();
  const isHandlingBackRef = useRef(false);
  const { top: topInset } = useSafeAreaInsets();
  const { productContent } = useProductContext();
  const { locale, t } = useI18n();
  const {
    data: blocks = [],
    refetch,
    isError,
    isFetching,
  } = useGetProductScreenBlocksQuery(
    client,
    { locale },
    { select: (data) => data.blockGroup?.blocks }
  );

  useMetaData('Product', { productName: productContent?.name });

  useFocusEffect(
    useCallback(() => {
      // Android is always edge-to-edge (transparent status bar), so the hero draws underneath it
      // and only the icon style needs to change.
      StatusBar.setBarStyle('light-content');
      return () => {
        StatusBar.setBarStyle('dark-content');
      };
    }, [])
  );

  const handleBack = useCallback(() => {
    if (shouldReturnToPurchaseOrderDetails) {
      isHandlingBackRef.current = true;
      const state = rootNavigation.getState();
      const previousRoutes = state.routes.slice(0, -1);

      rootNavigation.dispatch(
        CommonActions.reset({
          index: previousRoutes.length,
          routes: [
            ...previousRoutes,
            {
              name: 'PurchasesStack',
              state: {
                index: 1,
                routes: [
                  { name: 'PurchasesList' },
                  {
                    name: 'PurchaseOrderDetails',
                    params: {
                      entitlementKey: route.params.entitlementKey as string,
                    },
                  },
                ],
              },
            } as any,
          ],
        })
      );
      return;
    }

    rootNavigation.navigate('Destinations', undefined, { pop: true });
  }, [rootNavigation, route.params.entitlementKey, shouldReturnToPurchaseOrderDetails]);

  useEffect(() => {
    if (!shouldReturnToPurchaseOrderDetails) return undefined;

    return navigation.addListener('beforeRemove', (event) => {
      if (isHandlingBackRef.current) return;

      event.preventDefault();
      handleBack();
    });
  }, [handleBack, navigation, shouldReturnToPurchaseOrderDetails]);

  if (!productContent) return <Loading />;

  return (
    <Screen
      after={<LinkTicketsPrompt productContentKey={productContentKey} />}
      beforeContainer={
        <ProductHero
          beforeGradient={
            <>
              <HStack
                alignItems="center"
                justifyContent="space-between"
                position="absolute"
                pt={`${topInset}px`}
                top="0"
                w="full"
                zIndex={1}
              >
                <CustomHeaderBackButton
                  style={hasGlassHeaderButtons ? styles.alignedBackButton : styles.backButton}
                  testID={tID('BackButton')}
                  tintColor="white"
                  onPress={handleBack}
                />

                {Platform.OS !== 'web' && (
                  <ShareButton
                    accessibilityLabel={`Share ${productContent.name}`}
                    content={{
                      url: getUrl(productContent, locale),
                      title: productContent.name ?? '',
                    }}
                  />
                )}
              </HStack>
              <ProductLogo mb="12" mt="20" />
            </>
          }
          mb="8"
        >
          <ProductIntro />
        </ProductHero>
      }
    >
      <VStack space={PRODUCT_BLOCK_SPACE}>
        {/* Show spinner while fetching (including during retries) */}
        {isFetching && !blocks.length && <Spinner size="lg" />}
        {/* Show error only after retries are exhausted (not fetching anymore) */}
        {isError && !isFetching && !blocks.length && (
          <VStack space="3">
            <CPAlert status="error">{t('app_error_fetching_data')}</CPAlert>
            <Button isLoading={isFetching} onPress={() => refetch()}>
              {t('gen_try_again')}
            </Button>
          </VStack>
        )}
        {blocks.map((block) => (
          <ContentBlockRenderer key={block.id} space={PRODUCT_BLOCK_SPACE} {...block} />
        ))}
      </VStack>

      {__DEV__ && (
        <Button my="16" onPress={() => refetch()}>
          Refresh Content Blocks
        </Button>
      )}
    </Screen>
  );
}

ProductScreen.routeName = 'Home';
